import {
  getListCities,
  getListClassesNames, getListClientsCitiesStates,
  getListStates,
  getListUnits,
} from 'helpers/genericHelper';
import { getUserProfile } from 'helpers/userProfile';
import { ReactElement, useEffect, useState } from 'react';
import { useFilterContext } from '../../contexts/FilterContext';
import {
  BaseOptions, CityOption, ClientOption, FilterOptions, GroupOption, UnitOption,
} from '../../types';
import { Option } from '../SelectField';
import { FilterFields } from './FilterFields';
import { FilterFieldsContainer } from './styles';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { FilterButton } from './FilterButton';

export function Filter(): ReactElement {
  const [profile] = useState(getUserProfile);
  const [loading, setLoading] = useState(false);
  const {
    filterOptions,
    onSelectClient,
    setBaseOptions,
    setFilterOptions,
  } = useFilterContext();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const hasClientInput = !!(profile.manageAllClients || profile.manageMultipleClients);

  async function getOptions(): Promise<void> {
    setLoading(true);
    const [statesList, citiesList, unitsList] = await Promise.all([
      getListStates(),
      getListCities(),
      getListUnits(),
    ]);

    const newFilterOptions: FilterOptions = {
      states: [],
      groups: [],
      clients: [],
      cities: [],
      units: [],
    };

    const baseOptions: BaseOptions = {
      cities: [],
      units: [],
      states: [],
    };

    const profileManageMoreThanOneClient = profile.manageAllClients || profile.permissions.PER_CLIENT.length > 1;

    if (profileManageMoreThanOneClient) {
      const clientsList = await getListClientsCitiesStates();
      let allowedClients = clientsList;
      if (clientsList.length > 0) {
        if (!profile.manageAllClients) {
          const allowedIds = new Set(profile.permissions.PER_CLIENT.map((c) => c.clientId));

          allowedClients = clientsList.filter((c) => allowedIds.has(Number(c.value)));
        }

        newFilterOptions.clients.push(
          ...allowedClients.map(
            (item): ClientOption => ({
              label: item.name,
              value: item.value,
              iconName: 'User1Icon',
              cities: item.cities,
              states: item.states,
            }),
          ),
        );
      }
    } else {
      const groupList = await getListClassesNames();
      if (groupList.length > 0) {
        newFilterOptions.groups.push(
          ...groupList.map(
            (item): GroupOption => ({
              label: item.name,
              value: item.value,
              iconName: 'LayoutGrid1Icon',
              units: item.units,
            }),
          ),
        );
      }
    }

    if (statesList.length > 0) {
      const options = statesList.map(
        (item): Option => ({
          label: item.name,
          value: item.value,
          iconName: 'MarkerPin1Icon',
        }),
      );
      newFilterOptions.states.push(...options);

      baseOptions.states = options;
    }

    if (citiesList.length > 0) {
      const options = citiesList.map(
        (item): CityOption => ({
          label: item.name,
          value: item.value,
          stateId: item.stateId,
        }),
      );
      newFilterOptions.cities.push(...options);

      baseOptions.cities = options;
    }

    if (unitsList.length > 0) {
      const options = unitsList.map(
        (item): UnitOption => ({
          label: item.name,
          value: item.value,
          iconName: 'LayoutGrid1Icon',
          clientId: item.clientId,
          cityId: item.city,
          stateId: item.state,
        }),
      );
      newFilterOptions.units.push(...options);

      baseOptions.units = options;
    }

    setBaseOptions(baseOptions);
    setFilterOptions(newFilterOptions);
    handleClientByPermission();
    setLoading(false);
  }

  function handleClientByPermission(): void {
    const santanderIds = [21, 59, 120, 152];
    const isSantanderClient = profile.permissions.PER_CLIENT.some((p) => santanderIds.includes(p.clientId));

    if ((profile.manageAllClients || profile.manageMultipleClients) && !isSantanderClient) return;
    let clientId: number | null = null;

    if (isSantanderClient) {
      clientId = profile.permissions.PER_CLIENT[profile.permissions.PER_CLIENT.length - 1].clientId;
    } else {
      clientId = profile.permissions.PER_CLIENT[0].clientId;
    }

    if (clientId) {
      onSelectClient(clientId.toString());
    }
  }

  function onClickFilterButton(): void {
    setIsCollapsed((previousValue) => !previousValue);
  }

  useEffect(() => {
    getOptions();
  }, []);

  return (
    <FilterFieldsContainer hasClientInput={hasClientInput}>
      {isMobile && (
        <FilterButton
          collapsed={isCollapsed}
          onClick={onClickFilterButton}
          disabled={false}
        />
      )}
      {(!isMobile || !isCollapsed) && (
        <FilterFields
          filterOptions={filterOptions}
          disabled={loading}
        />
      )}
    </FilterFieldsContainer>
  );
}
